<template>
    <unsubscribe-card>
        <logo white :society="false"></logo>
        <div v-if="!loading" class="unsubscribe-content">
            <!--
                First page
            -->
            <section v-if="!mode">
                <h1>{{ $t('unsubscribePage.consideringUnsubscribing') }}</h1>
                <p>
                    {{ $t('unsubscribePage.stayConnected') }}
                </p>
                <p>
                    {{ $t('unsubscribePage.chooseLowerFrequency') }}
                </p>
                <div class="unsubscribe-buttons">
                    <b-button variant="white" @click="updateMode('frequency')">
                        {{ $t('unsubscribePage.updateFrequencyButton') }}
                    </b-button>
                    <b-button variant="white" @click="updateMode('pause')">
                        {{ $t('unsubscribePage.pauseSubscriptionButton') }}
                    </b-button>
                    <b-button variant="white" @click="updateMode('unsubscribe')">
                        {{ $t('unsubscribePage.unsubscribeButton') }}
                    </b-button>
                </div>
            </section>
            <!--
                Email Frequency
            -->
            <div v-if="mode === 'frequency'" class="frequency-section">
                <h1>{{ $t('unsubscribePage.frequencyFirstStepTitle') }}</h1>
                <b-form-group>
                    <b-form-radio v-model="member.email_frequency" :value="2">
                        <div class="font-weight-normal">{{ $t('members.profileEditor.emailFrequencyNormalLabel') }}</div>
                        <div class="small font-weight-lighter">{{ $t('members.profileEditor.emailFrequencyNormalDesc') }}</div>
                    </b-form-radio>
                    <b-form-radio v-model="member.email_frequency" :value="1">
                        <div class="font-weight-normal">{{ $t('members.profileEditor.emailFrequencySlowLabel') }}</div>
                        <div class="small font-weight-lighter">{{ $t('members.profileEditor.emailFrequencySlowDesc') }}</div>
                    </b-form-radio>
                </b-form-group>
                <b-button block variant="white" @click="updateFrequency">
                    {{ $t('unsubscribePage.frequencySubmitButton') }}
                </b-button>
            </div>
            <!--
                Pause
            -->
            <div v-if="mode === 'pause'" class="pause-section">
                <h1>
                    {{ $t('unsubscribePage.pauseConfirmTitle') }}
                </h1>
                <p>
                    {{ $t('unsubscribePage.pauseConfirmText') }}
                </p>
            </div>
            <!--
                Unsubscribe / Feedback
            -->
            <div v-if="mode === 'unsubscribe'" class="unsubscribe-feedback-section">
                <h1>{{ $t('unsubscribePage.feedbackTitle') }}</h1>
                <p>{{ $t('unsubscribePage.feedbackQuestion') }}</p>
                <div class="feedback-checkboxes">
                    <b-radio-group v-model="feedback.selected" :options="feedback.options"></b-radio-group>
                </div>
                <div v-if="selectedOther" class="mb-3">
                    <b-form-group>
                        <b-textarea v-model="feedback.text"></b-textarea>
                    </b-form-group>
                </div>
                <b-button block variant="white" @click="sendFeedback">
                    {{ $t('unsubscribePage.feedbackSendForm') }}
                    <b-spinner v-if="loading" small></b-spinner>
                </b-button>
            </div>

            <div v-if="mode === 'thanks'" class="thank-you-section">
                <h1>{{ $t('unsubscribePage.thankYou') }}</h1>
            </div>
            <div v-if="mode === 'error'" class="error-section">
                <h1>{{ $t('unsubscribePage.errorModeMessage') }}</h1>
            </div>

            <div v-if="mode !== null">
                <b-button block variant="white-outline" @click="mode = null">
                    {{ $t('unsubscribePage.goBack') }}
                </b-button>
            </div>
        </div>
        <div class="loading-wrapper" v-if="loading">
            <b-spinner></b-spinner>
        </div>
    </unsubscribe-card>
</template>
<script>
import { defineComponent } from 'vue';
import UnsubscribeCard from '@components/Members/UnsubscribeCard.vue';
import Logo from '@components/App/Logo.vue';
import { useMemberStore } from '@stores/Member';

export default defineComponent({
    components: { UnsubscribeCard, Logo },
    async mounted() {
        this.voyadoId = this.$route.params.uuid;
        this.messageId = this.$route.query.message;

        this.loading = true;
        const member = useMemberStore();
        const res = await member.getMember(this.voyadoId);
        this.member = res.data;
        this.loading = false;
    },
    computed: {
        selectedOther() {
            return this.feedback.selected.includes('other');
        },
    },
    data() {
        const $t = this.$t;
        return {
            voyadoId: null,
            messageId: null,
            mode: null,
            loading: false,
            member: null,
            feedback: {
                selected: [],
                text: null,
                options: [
                    {
                        text: $t('unsubscribePage.feedbackOptions.tooManyNewsletters'),
                        value: 'too_many_newsletters'
                    },
                    {
                        text: $t('unsubscribePage.feedbackOptions.contentNotRelevant'),
                        value: 'content_not_relevant'
                    },
                    {
                        text: $t('unsubscribePage.feedbackOptions.productsProgramNotRelevant'),
                        value: 'products_not_relevant'
                    },
                    {
                        html: $t('unsubscribePage.feedbackOptions.loyaltyProgramNotRelevant'),
                        value: 'loyalty_program_not_relevant'
                    },
                    {
                        text: $t('unsubscribePage.feedbackOptions.other'),
                        value: 'other'
                    }
                ]
            },
        };
    },
    methods: {
        async getMember() {
        },
        async sendFeedback() {
            const member = useMemberStore();

            this.loading = true;
            try {
                await member.sendFeedback(this.voyadoId, {
                    reasons: this.feedback.selected,
                    text: this.feedback.text,
                });
                this.mode = 'thanks';
            } catch (e) {
               this.mode = 'error';
            }
            this.loading = false;
        },
        async updateMode(mode) {
            const member = useMemberStore();

            if (mode === 'pause') {
                this.loading = true;
                try {
                    await member.pauseSubscription(this.voyadoId, this.messageId);
                } catch (e) {
                   this.mode = 'error';
                }
                this.loading = false;
            }

            if (mode === 'unsubscribe') {
                this.loading = true;
                try {
                    await member.unsubscribe(this.voyadoId, this.messageId);
                } catch (e) {
                   this.mode = 'error';
                }
                this.loading = false;
            }

            if (this.mode === 'error') {
                return;
            }

            this.mode = mode;
        },
        async updateFrequency() {
            const member = useMemberStore();

            this.loading = true;
            try {
                await member.updateCommunication(this.voyadoId, this.member);
                this.mode = 'thanks';
            } catch (e) {
                this.mode = 'error';
            }
            this.loading = false;
        },
    },
});
</script>
<style scoped>
.unsubscribe-content {
    display: grid;
    text-align: center;
    padding: 3rem;
    gap: 0.5rem;

    h1 {
        font-size: 1.75rem;
        font-family: Chronicle, serif;
        font-weight: 400;
    }

    .sm + & {
        padding: 3rem 1rem;
    }
}

.unsubscribe-buttons {
    display: grid;
    gap: 1rem;
    margin-top: 2rem;
}

:deep(.feedback-checkboxes a) {
    color: white;
    text-decoration: underline;
    &:hover {
        color: red;
        opacity: 0.7;
    }
}

:deep(.btn-white-outline) {
    color: white;
    text-decoration: underline;

    &:hover {
        color: white;
    }
}

.frequency-section {
    text-align: left;

    :deep(.custom-radio) {
        margin-bottom: 1rem;
    }
}

.feedback-checkboxes {
    text-align: left;
    margin-bottom: 2rem;

    p {
        margin: 1.5rem 0;
    }

    :deep(.custom-checkbox) {
        margin-bottom: 0.25rem;
    }
}

.loading-wrapper {
    display: flex;
    justify-content: center;
    align-items: center;
    min-height: 200px;
    width: 100%;
}

::v-deep .btn {
    margin-bottom: 0;
    text-transform: uppercase;
    font-size: 14px;
}

::v-global(.unsubscribe-page .content) {
    min-height: 100vh;
}

::v-global(.unsubscribe-page .logo) {
    max-width: 200px;
}
</style>
